'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Link from 'next/link';
import { HorizontalLanguageSelector } from '@/components/horizontal-language-selector';

const CONSENT_STORAGE_KEY = 'starky-consent';
const CONSENT_VERSION = '1.0';

interface ConsentData {
  version: string;
  timestamp: number;
  accepted: boolean;
}

interface ConsentModalProps {
  children?: React.ReactNode;
  onConsentGiven?: () => void;
}

export function ConsentModal({ children, onConsentGiven }: ConsentModalProps) {
  const [showModal, setShowModal] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const t = useTranslations('consent');

  useEffect(() => {
    // DEBUG: Clear consent for testing PWA
    // try {
    //   localStorage.removeItem(CONSENT_STORAGE_KEY);
    // } catch (error) {
    //   console.error('Error clearing consent for debug:', error);
    // }

    // Check if user has already given consent
    const checkConsent = () => {
      try {
        const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (stored) {
          const consentData: ConsentData = JSON.parse(stored);
          // Check if consent is still valid (same version and accepted)
          if (consentData.version === CONSENT_VERSION && consentData.accepted) {
            setHasConsent(true);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking consent:', error);
      }

      // No valid consent found, show modal
      setShowModal(true);
      setHasConsent(false);
    };

    checkConsent();
  }, []);

  const handleAcceptConsent = () => {
    try {
      const consentData: ConsentData = {
        version: CONSENT_VERSION,
        timestamp: Date.now(),
        accepted: true,
      };

      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentData));
      setHasConsent(true);
      setShowModal(false);

      // Notify parent component
      onConsentGiven?.();
    } catch (error) {
      console.error('Error storing consent:', error);
      // Continue anyway - don't block the user
      setHasConsent(true);
      setShowModal(false);
      onConsentGiven?.();
    }
  };

  // Don't render anything until we've checked consent
  if (!hasConsent && !showModal) {
    return null;
  }

  // If consent is given, render children
  if (hasConsent) {
    return <>{children}</>;
  }

  // Show consent modal
  return (
    <Dialog open={showModal} onOpenChange={() => { }} modal>
      <DialogContent
        className="sm:max-w-md max-h-[90vh] max-w-[calc(100vw-2rem)] flex flex-col overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
      >
        <DialogHeader className="space-y-3 w-full min-w-0 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-center">
            {t('title')}
          </DialogTitle>

          <HorizontalLanguageSelector />

          <DialogDescription className="text-start">
            {t('titleDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 min-h-0 -mx-6 px-6">
          <ul className="space-y-2 text-sm list-disc list-outside ml-4 pl-2">
            <li>{t('aiTranslation')}</li>
            <li>{t('privacyMessage')}</li>
            <li>{t('dataProcessing')}</li>
            <li>{t('cookieUsage')}</li>
          </ul>

          <div className="flex flex-wrap gap-4">
            <Link
              href="/terms"
              rel="noopener noreferrer"
              className="text-primary hover:underline text-sm font-medium"
            >
              {t('termsOfService')}
            </Link>
            <Link
              href="/privacy"
              rel="noopener noreferrer"
              className="text-primary hover:underline text-sm font-medium"
            >
              {t('privacyPolicy')}
            </Link>
          </div>

          <p className="text-sm text-muted-foreground">
            {t('agreementText')}
          </p>
        </div>

        <div className="flex justify-center pt-4 flex-shrink-0">
          <Button
            onClick={handleAcceptConsent}
            className="w-full sm:w-auto px-8"
            size="lg"
          >
            {t('acceptButton')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to check consent status
export function useConsent() {
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored) {
        const consentData: ConsentData = JSON.parse(stored);
        setHasConsent(
          consentData.version === CONSENT_VERSION && consentData.accepted
        );
      }
    } catch (error) {
      console.error('Error checking consent:', error);
    }
  }, []);

  return hasConsent;
}

// Utility to clear consent (for testing or policy updates)
export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing consent:', error);
  }
}
